/**
 * Главная вики — витрина: заголовок и поиск на первом экране рядом с парящим островом
 * из блоков, статья-приветствие (её выбирает админ в управлении) с колонкой разделов справа,
 * внизу «Рекомендуем», «Популярные статьи» и «Вы недавно открывали».
 *
 * Единственная постановочная анимация вики — здесь: при первом за сессию открытии
 * главной остров собирается из падающих блоков, а заголовок и поиск проявляются следом.
 */
import { el, fmtNumber, icon, play, reducedMotion } from "../../js/ui.js";
import { articlePath, plural } from "./content.js";
import { articleBySlug, featuredArticles, fetchArticle, popularArticles, recentArticles, wiki } from "./data.js";
import { enhanceArticle } from "./enhance.js";
import { topicCards } from "./pages.js";
import { openSearch } from "./shell.js";

const INTRO_KEY = "jk_wiki_intro_played";
const IS_MAC = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** Собрать главную. Возвращает описание экрана для app.js. */
export async function renderHome() {
  const homeSlug = wiki.settings.public_home_slug || "home";
  const welcome = articleBySlug(homeSlug) ? await fetchArticle(homeSlug) : null;
  const stage = el("div", { class: "wiki-hero__stage", "aria-hidden": "true" });
  const heroText = heroBlock();
  const intro = claimIntro();
  // Текст первого экрана до начала анимации скрыт, иначе он мелькнул бы до проявления.
  if (intro) for (const child of heroText.children) child.style.opacity = "0";

  const sections = el("section", { class: "wiki-home__sections", "aria-labelledby": "wiki-sections-title" }, [
    el("h2", { id: "wiki-sections-title", class: "wiki-home__heading" }, "Разделы"),
    topicCards("public", { exclude: homeSlug }),
  ]);
  const node = el("div", { class: "wiki-home" }, [
    el("section", { class: "wiki-hero" }, [heroText, stage]),
    // Приветствие слева, разделы колонкой справа; без статьи-приветствия разделы во всю ширину.
    welcome && welcome.ok
      ? el("div", { class: "wiki-home__grid" }, [welcomeBlock(welcome.data), sections])
      : sections,
    el("div", { class: "wiki-home__rails" }, [
      rail("Рекомендуем", "star", featuredArticles().slice(0, 5).map((article) => railLink(article.slug, article.title, article.summary))),
      rail("Популярные статьи", "eye", popularArticles(5).map((article) =>
        railLink(article.slug, article.title, `${fmtNumber(article.views)} ${plural(article.views, "просмотр", "просмотра", "просмотров")}`))),
      rail("Вы недавно открывали", "clock", recentArticles(5).map((item) => railLink(item.slug, item.title))),
    ]),
  ]);

  return {
    node,
    title: null,
    onShown: async () => {
      if (intro) {
        [...heroText.children].forEach((child, index) =>
          play(child, { opacity: [0, 1], transform: ["translateY(12px)", "none"] }, { duration: 0.5, delay: 0.35 + index * 0.08, ease: [0.22, 1, 0.36, 1] })
            .then(() => { child.style.opacity = ""; }));
      }
      // Остров — отдельная часть сборки: скачивается после отрисовки главной и строится,
      // когда браузер свободен, — первый экран не ждёт трёхмерную сцену.
      const { mountIsland } = await import("./island.js");
      const whenIdle = window.requestIdleCallback || ((task) => setTimeout(task, 200));
      whenIdle(() => { if (stage.isConnected) mountIsland(stage, { assemble: intro }); }, { timeout: 1500 });
    },
  };
}

/**
 * Играть ли вступление: только при первом открытии главной за сессию браузера и если в
 * системе не включено «меньше движения». Отмечает, что вступление уже было.
 */
function claimIntro() {
  if (reducedMotion()) return false;
  try {
    if (sessionStorage.getItem(INTRO_KEY)) return false;
    sessionStorage.setItem(INTRO_KEY, "1");
    return true;
  } catch (storageError) {
    return false;
  }
}

/** Заголовок, пояснение и кнопка поиска, похожая на поле ввода. */
function heroBlock() {
  const search = el("button", { type: "button", class: "wiki-hero__search" }, [
    icon("search", 20),
    el("span", { class: "flex-1 text-left" }, "Найти команду, механику или статью"),
    el("kbd", { class: "wiki-kbd", "aria-hidden": "true" }, IS_MAC ? "⌘ K" : "Ctrl K"),
  ]);
  search.addEventListener("click", () => openSearch());
  return el("div", { class: "wiki-hero__text" }, [
    el("h1", { class: "wiki-hero__title" }, "Вики JustKiwi"),
    el("p", { class: "wiki-hero__lede" }, "Гайды по режимам, командам и механикам сервера: как начать, чем заняться и где что найти."),
    search,
  ]);
}

/** Статья-приветствие: её первый заголовок становится заголовком блока. */
function welcomeBlock(article) {
  const prose = el("div", { class: "wiki-prose", html: article.renderedHtml || "" });
  const first = prose.firstElementChild;
  if (first && first.tagName === "H1") first.replaceWith(el("h2", { class: "wiki-home__welcome-title" }, first.textContent));
  enhanceArticle(prose, { interactive: false });
  return el("section", { class: "wiki-home__welcome" }, prose);
}

/** Колонка ссылок под приветствием и разделами. Пустая колонка не показывается. */
function rail(title, iconName, items) {
  if (!items.length) return null;
  return el("section", { class: "wiki-rail" }, [
    el("h2", { class: "wiki-rail__title" }, [icon(iconName, 16), title]),
    el("ul", { class: "wiki-rail__list" }, items.map((item) => el("li", null, item))),
  ]);
}

function railLink(slug, title, hint) {
  return el("a", { href: articlePath(slug), class: "wiki-rail__link" }, [
    el("span", { class: "wiki-rail__name" }, title),
    hint ? el("span", { class: "wiki-rail__hint" }, hint) : null,
  ]);
}
